import React from "react";
import {
  dayDiff,
  daysPastDue,
  dueDateKey,
  formatDueKeyShort,
  orgTodayKey,
} from "../../lib/portalItems";

// =============================================================================
// CLIENT PORTAL HELPERS
// =============================================================================
// PURPOSE: Shared utilities for Client Portal components (TaskSection, FileSection, CommunicationSection).
//
// EXPORTS:
// - showToast(): Display temporary success/error notifications (DOM-based, no React state)
// - formatDate(): Human-readable date formatting (Today, Tomorrow, Yesterday, or "Jan 15")
// - getFileIcon(): SVG icon based on file type (PDF, Excel, Word, image, video, etc.)
// - getCommIcon(): SVG icon based on communication type (Zoom, Email, Slack)
// - TASK_STATUS: Constants for task status values (snake_case to match DB CHECK constraint)
//
// DESIGN DECISION: Pure functions, no React hooks. Can be used in any component
// without requiring a React context. Simple, testable, reusable.
// =============================================================================

// Status values matching DB CHECK constraint: ('pending', 'in_progress', 'completed', 'cancelled')
// IMPORTANT: These MUST be lowercase snake_case — the DB client_tasks_status_check constraint
// rejects any other casing. PascalCase values ("Pending", "Completed") = Postgres error 23514.
export const TASK_STATUS = {
  PENDING: "pending" as const,
  IN_PROGRESS: "in_progress" as const,
  COMPLETED: "completed" as const,
  CANCELLED: "cancelled" as const,
};

// Portal task ASSIGNEES = execs + the client only. Reps (closer/setter/dialer/
// manager) are never assignable — we don't give reps portal tasks. Tolerates the
// legacy `client_owner` value (normalized to `client` at auth elsewhere) and
// excludes null/unknown roles by default (allowlist, not denylist).
// Used by BOTH assignee pickers (TaskDetailModal + TaskSection). The @mention
// picker (ActivityFeed) is intentionally NOT filtered — reps stay mentionable.
export function isPortalAssignableRole(role: string | null | undefined): boolean {
  const r = (role ?? "").toLowerCase();
  return r === "exec" || r.startsWith("client");
}

// Optional action button (e.g. Undo) rendered inside the toast.
// expiresMs defaults to 5000ms; passing a value overrides for both the button and auto-dismiss.
export interface ToastAction {
  label: string;
  onClick: () => void;
  expiresMs?: number;
}

// Simple toast notification
// bg-ds-red / bg-ds-green can't be used here — dynamic className strings are
// not scanned by Tailwind at compile time. Use CSS variables directly instead.
export function showToast(
  message: string,
  type: "success" | "error" = "success",
  action?: ToastAction
) {
  const toast = document.createElement("div");
  // z-[10003]: above the task modal backdrop (z-[9999]), HistoryPanel (10000/10001)
  // and EditWarningToast (10002). At z-50 every toast fired from inside an open
  // modal (subtask save failed, undelete failed...) rendered under the blur.
  toast.className =
    "fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-[10003] transition-opacity duration-300 text-glass flex items-center gap-3";
  toast.setAttribute("data-testid", "app-toast");
  toast.style.background = type === "error" ? "var(--red)" : "var(--green)";

  const msgSpan = document.createElement("span");
  msgSpan.textContent = message;
  toast.appendChild(msgSpan);

  // 5s persistence (was 3s) — leaves enough headroom for Playwright assertions
  // that follow a slow Supabase write (auth.getUser + RLS-checked INSERT).
  const lifeMs = action?.expiresMs ?? 5000;
  // timerRef is a mutable box so cleanup() can clear the timer set after cleanup is defined.
  const timerRef: { id: ReturnType<typeof setTimeout> | undefined } = { id: undefined };

  const cleanup = () => {
    if (timerRef.id) clearTimeout(timerRef.id);
    toast.style.opacity = "0";
    setTimeout(() => toast.remove(), 300);
  };

  if (action) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.textContent = action.label;
    btn.className = "underline font-semibold cursor-pointer anim-tap";
    btn.setAttribute("data-testid", "app-toast-action");
    btn.style.background = "transparent";
    btn.style.border = "0";
    btn.style.color = "inherit";
    btn.onclick = (e) => {
      e.stopPropagation();
      cleanup();
      action.onClick();
    };
    toast.appendChild(btn);
  }

  document.body.appendChild(toast);
  timerRef.id = setTimeout(cleanup, lifeMs);
}

export function formatDate(date: Date, tz?: string): string {
  // Compare calendar days (not timestamps) to avoid timezone/time-of-day bugs
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((dateStart.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(tz ? { timeZone: tz } : {}),
  });
}

export function getFileIcon(fileType: string): React.ReactNode {
  const type = fileType?.toLowerCase() || "";
  const cls = "w-5 h-5 text-glass-muted";
  if (type.includes("pdf"))
    return (
      <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
        />
      </svg>
    );
  if (type.includes("xls") || type.includes("csv") || type.includes("spreadsheet"))
    return (
      <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 18.375c0-.621.504-1.125 1.125-1.125"
        />
      </svg>
    );
  if (type.includes("doc"))
    return (
      <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
        />
      </svg>
    );
  if (type.includes("png") || type.includes("jpg") || type.includes("image"))
    return (
      <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a1.5 1.5 0 001.5-1.5V4.5a1.5 1.5 0 00-1.5-1.5H3.75a1.5 1.5 0 00-1.5 1.5v15a1.5 1.5 0 001.5 1.5z"
        />
      </svg>
    );
  if (type.includes("loom") || type.includes("video"))
    return (
      <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
        />
      </svg>
    );
  return (
    <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13"
      />
    </svg>
  );
}

export function getCommIcon(commType: string): React.ReactNode {
  const cls = "w-5 h-5 text-glass-muted";
  switch (commType) {
    case "Zoom":
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
          />
        </svg>
      );
    case "Email":
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
          />
        </svg>
      );
    case "Slack":
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155"
          />
        </svg>
      );
    default:
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.815a4.5 4.5 0 00-6.364-6.364L4.757 8.25a4.5 4.5 0 006.364 6.364l4.5-4.5z"
          />
        </svg>
      );
  }
}

/**
 * Returns a label and style variant for a task's due date pill.
 * Used by TaskRow to render colored date chips.
 */
export function getDueDatePill(
  dueDate: string | undefined,
  isCompleted: boolean,
  tz: string | null | undefined
): {
  label: string;
  variant: "overdue" | "today" | "tomorrow" | "future" | "done" | "none";
} {
  if (isCompleted) return { label: "Done", variant: "done" };
  if (!dueDate) return { label: "", variant: "none" };

  // Calendar-key math via the shared due-date engine (lib/portalItems.ts) — no
  // Date parsing of the date-only string, so no day shift in any timezone.
  const key = dueDateKey(dueDate);
  if (!key) return { label: "", variant: "none" };
  const diffDays = dayDiff(orgTodayKey(tz), key);

  if (diffDays < 0) {
    const absDays = Math.abs(diffDays);
    return {
      label: absDays === 1 ? "1 day ago" : `${absDays} days ago`,
      variant: "overdue",
    };
  }
  if (diffDays === 0) return { label: "Today", variant: "today" };
  if (diffDays === 1) return { label: "Tomorrow", variant: "tomorrow" };

  return { label: formatDueKeyShort(key), variant: "future" };
}

/**
 * Whole-day overdue count for a raw YYYY-MM-DD due-date string.
 * Positive = N days past due, 0 = due today, negative = due in the future,
 * null = no due date. Parses as LOCAL midnight (new Date(y, m-1, d)) — NEVER
 * new Date(string) — so a due date never shifts a day for orgs east of UTC
 * (/debug #16 locale dates, #2 timezone; same reason portalItems keeps
 * dueDateString raw). Distinct from getDueDatePill above, which produces a
 * label + a coarser variant; these two are the numeric day-count + red/amber
 * threshold split the KPI strip + List rows need. They coexist by design.
 */
export function daysOverdue(
  dueDateString: string | null | undefined,
  tz: string | null | undefined
): number | null {
  // "Today" is the org's calendar day when tz is given (viewer's otherwise).
  // Prefer PortalItem.daysOverdue where a PortalItem exists — it is precomputed
  // with the org tz so every view agrees.
  return daysPastDue(dueDateString, orgTodayKey(tz));
}

/**
 * Bucket a daysOverdue() value into an urgency token used to color the KPI
 * strip + List-row urgency stripe/date. red = 10+ days over (needs attention),
 * amber = 4–9 (slipping), over = 0–3 (just tipped past due / due today),
 * future = upcoming or no date.
 */
export function urgencyToken(over: number | null): "red" | "amber" | "over" | "future" {
  if (over == null) return "future";
  if (over >= 10) return "red";
  if (over >= 4) return "amber";
  if (over >= 0) return "over";
  return "future";
}

/**
 * Format a raw YYYY-MM-DD due-date string as a short "Mon D" label (en-US),
 * parsed at LOCAL midnight so the calendar day never shifts for orgs east of
 * UTC. Returns "" for null/malformed input. Shared by KanbanCard + ListView.
 */
export function formatDueShort(dueDateString: string | null | undefined): string {
  if (!dueDateString) return "";
  const [y, m, d] = dueDateString.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
